/* ============================================================
   F.G. EDWARDS — Journees poules COLLECTION : interactive tools
   Vanilla JS, no deps, no build. All collection-template behaviour
   lives here (loaded defer once by jp-collection-hero; each tool
   section also links it, and a window guard makes the extra loads a
   no-op). Every block no-ops safely when its section is absent.
   Handles:
     1. Egg rainbow  (#jp-rainbow) : click an egg -> smooth-scroll to
        the breed grid and filter it by that egg colour; anchor href
        stays as the no-JS fallback. GA4 jpc_rainbow_click {breed}.
     2. Breed grid filter chips (parallel-agent contract) :
        data-jpc-filter-egg / -care / -type / -reset toggle over
        cards data-jpc-card [data-egg|data-care|data-btype];
        [data-jpc-empty] shows when nothing matches.
     3. Flock quiz (#jp-quiz) : 3-question state machine + a scoring
        engine (pickFlock) that ranks every breed from its own fiche,
        so no breed is ever named in this file. Result = ranked picks
        with reasons, count line from Q2, one-tap flock reservation
        in season, priority-list CTA out of season.
        GA4 jpc_quiz_start / jpc_quiz_complete {q1,q2,q3,flock}.
     4. Availability matrix links : data-jpc-matrix-link ->
        GA4 jpc_matrix_click.
     5. Rainbow basket ([data-jpc-rainbow-basket]) : one tap adds three
        breeds with three egg colours for the same day and store. Reads
        the quiz breed map. GA4 jpc_rainbow_basket_add.
     6. Sold-out substitution : each sold-out card names the closest
        breed still reservable (egg colour first, then temperament).
   GA4 is always guarded behind a window.gtag check.
   ============================================================ */
(function () {
  'use strict';
  if (window.__jpCollection) return;
  window.__jpCollection = true;

  var GRID_ID = 'jp-breed-grid';

  function ga(name, params) {
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', name, params || {}); } catch (e) { /* noop */ }
    }
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function parseJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent || ''); } catch (e) { return null; }
  }

  function scrollToEl(el) {
    if (!el || !el.getBoundingClientRect) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - 16;
    window.scrollTo({ top: top, behavior: 'smooth' });
  }

  /* ---------- 2. Breed grid filter (shared by chips + rainbow) ---------- */
  var gridFilter = { egg: null, care: null, type: null };

  function grid() { return document.getElementById(GRID_ID); }

  function syncChips() {
    var dims = ['egg', 'care', 'type'];
    dims.forEach(function (dim) {
      var attr = 'data-jpc-filter-' + dim;
      var chips = document.querySelectorAll('[' + attr + ']');
      chips.forEach(function (c) {
        c.setAttribute('aria-pressed', c.getAttribute(attr) === gridFilter[dim] ? 'true' : 'false');
      });
    });
  }

  function applyGridFilter() {
    var g = grid();
    if (!g) return;
    var cards = g.querySelectorAll('[data-jpc-card]');
    var visible = 0;
    cards.forEach(function (card) {
      var okEgg = !gridFilter.egg || card.getAttribute('data-egg') === gridFilter.egg;
      var okCare = !gridFilter.care || card.getAttribute('data-care') === gridFilter.care;
      var okType = !gridFilter.type || card.getAttribute('data-btype') === gridFilter.type;
      var show = okEgg && okCare && okType;
      if (show) { card.removeAttribute('data-jpc-hidden'); visible++; }
      else { card.setAttribute('data-jpc-hidden', 'true'); }
    });
    var empty = g.querySelector('[data-jpc-empty]') || document.querySelector('[data-jpc-empty]');
    if (empty) empty.hidden = visible !== 0;
    syncChips();
  }

  function initGridFilters() {
    var chips = document.querySelectorAll('[data-jpc-filter-egg],[data-jpc-filter-care],[data-jpc-filter-type],[data-jpc-filter-reset]');
    if (!chips.length) return;
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        clearQuizHighlight();
        if (chip.hasAttribute('data-jpc-filter-reset')) {
          gridFilter = { egg: null, care: null, type: null };
          applyGridFilter();
          return;
        }
        ['egg', 'care', 'type'].forEach(function (dim) {
          var attr = 'data-jpc-filter-' + dim;
          if (chip.hasAttribute(attr)) {
            var val = chip.getAttribute(attr);
            gridFilter[dim] = (gridFilter[dim] === val) ? null : val; /* toggle */
          }
        });
        applyGridFilter();
      });
    });
  }

  /* ---------- 1. Egg rainbow ---------- */
  function initRainbow() {
    var eggs = document.querySelectorAll('[data-jpc-rainbow]');
    if (!eggs.length) return;
    eggs.forEach(function (egg) {
      egg.addEventListener('click', function (e) {
        var breed = egg.getAttribute('data-breed-code') || '';
        var eggName = egg.getAttribute('data-egg') || '';
        ga('jpc_rainbow_click', { breed: breed });
        var g = grid();
        if (!g) return; /* no grid on page: let the anchor navigate to the PDP */
        e.preventDefault();
        clearQuizHighlight();
        gridFilter.egg = eggName || null;
        applyGridFilter();
        scrollToEl(g);
      });
    });
  }

  /* ---------- 3. Flock quiz ---------- */
  /* Recommendation engine. Nothing here names a breed: every breed is
     scored from its own fiche (eggs/year, kids, docility, winter, noise,
     care level, dual purpose), so a new breed enters the quiz the day its
     fiche is filled. Q1 sets the base weights, Q3 shifts them and applies
     a penalty to breeds that clearly miss the stated constraint. The pick
     then favours different egg colours so a flock is never three times
     the same hen. */
  var Q1_WEIGHTS = {
    famille:    { kid: 3, doc: 2, easy: 2, eggs: 1, cold: 1, quiet: 0.5, dual: 0 },
    production: { kid: 0, doc: 0.5, easy: 1, eggs: 4, cold: 1, quiet: 0, dual: 0 },
    couleurs:   { kid: 1, doc: 1, easy: 1, eggs: 1, cold: 0.5, quiet: 0, dual: 0 },
    double:     { kid: 0, doc: 1, easy: 0.5, eggs: 1, cold: 1.5, quiet: 0, dual: 4 }
  };
  var Q3_SHIFT = {
    enfants: { kid: 3, doc: 1.5 },
    hiver:   { cold: 3.5 },
    voisins: { quiet: 3.5 },
    facile:  { easy: 2.5, doc: 1.5 }
  };
  var Q3_FLOOR = { enfants: 'kid', hiver: 'cold', voisins: 'noise' }; /* raw 1-5 field */
  var RESULT_KEY = { famille: 'famille', production: 'production', couleurs: 'rainbow', double: 'double' };

  /* Shell colours are compared in CIE Lab, where distance tracks what the
     eye sees: a pale blue and a cream are both "light" in RGB but clearly
     different eggs in a carton. */
  function rgb(hex) {
    var m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || '');
    if (!m) return null;
    var c = [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)].map(function (v) {
      v /= 255; return v > 0.04045 ? Math.pow((v + 0.055) / 1.055, 2.4) : v / 12.92;
    });
    var x = (c[0] * 0.4124 + c[1] * 0.3576 + c[2] * 0.1805) / 0.95047;
    var y = (c[0] * 0.2126 + c[1] * 0.7152 + c[2] * 0.0722);
    var z = (c[0] * 0.0193 + c[1] * 0.1192 + c[2] * 0.9505) / 1.08883;
    function f(t) { return t > 0.008856 ? Math.pow(t, 1 / 3) : (7.787 * t + 16 / 116); }
    return [116 * f(y) - 16, 500 * (f(x) - f(y)), 200 * (f(y) - f(z))]; /* L, a, b */
  }
  function dist(p, q) { /* hue and chroma count double: lightness alone is not a new colour */
    var dl = (p[0] - q[0]) * 0.5, da = p[1] - q[1], db = p[2] - q[2];
    return Math.min(1, Math.sqrt(dl * dl + da * da + db * db) / 60);
  }
  function num(v) { var n = parseFloat(v); return isNaN(n) ? null : n; }
  function five(v) { var n = num(v); return n === null ? 0.5 : Math.max(0, Math.min(1, n / 5)); }

  function traits(info) {
    var eggs = num(info.eggs);
    return {
      kid: five(info.kid),
      doc: five(info.doc),
      cold: five(info.cold),
      quiet: five(info.noise),
      easy: info.care === 'debutant' ? 1 : (info.care ? 0.35 : 0.5),
      dual: info.dual ? 1 : 0,
      /* 200 eggs = 0, 330 eggs = 1 : spreads real layers apart */
      eggs: eggs ? Math.max(0, Math.min(1, (eggs - 200) / 130)) : 0.4
    };
  }

  function pickFlock(a, map) {
    var q1 = Q1_WEIGHTS[a.q1] ? a.q1 : 'famille';
    var w = {}; var k;
    for (k in Q1_WEIGHTS[q1]) w[k] = Q1_WEIGHTS[q1][k];
    var shift = Q3_SHIFT[a.q3] || {};
    for (k in shift) w[k] = (w[k] || 0) + shift[k];

    var codes = Object.keys(map);
    var anyBuyable = codes.some(function (c) { return map[c].variants && map[c].variants.length; });

    /* how many breeds share each egg colour (rare colours matter for "couleurs") */
    var colourCount = {};
    codes.forEach(function (c) { var n = map[c].name || c; colourCount[n] = (colourCount[n] || 0) + 1; });

    var scored = codes.map(function (c) {
      var info = map[c]; var t = traits(info); var total = 0; var parts = {};
      for (var key in w) { parts[key] = w[key] * t[key]; total += parts[key]; }
      var floorField = Q3_FLOOR[a.q3];
      if (floorField) {
        var raw = num(info[floorField]);
        if (raw !== null && raw <= 2) total *= 0.45;      /* clearly misses the constraint */
        else if (raw !== null && raw === 3) total *= 0.85;
      }
      /* in season, lean to what can be reserved today so the result is actionable */
      if (anyBuyable && info.variants && info.variants.length) total *= 1.25;
      return { code: c, score: total, parts: parts, t: t };
    });

    /* "oeufs et viande" only ever proposes real dual-purpose breeds */
    if (q1 === 'double') scored = scored.filter(function (sc) { return map[sc.code].dual; });
    var want = q1 === 'couleurs' ? 4 : 3;
    var picked = []; var usedColour = {};

    if (q1 === 'couleurs') {
      /* A colourful carton is about eggs that LOOK different, so the pick
         is driven by the real shell colours: each next breed is the one
         whose egg is furthest from those already chosen, tempered by how
         well the breed fits the visitor. Three shades of brown never win. */
      var maxScore = 0;
      scored.forEach(function (sc) { if (sc.score > maxScore) maxScore = sc.score; });
      var mean = [0, 0, 0]; var n = 0;
      scored.forEach(function (sc) { var c = rgb(map[sc.code].hex); if (c) { mean[0] += c[0]; mean[1] += c[1]; mean[2] += c[2]; n++; } });
      if (n) { mean = [mean[0] / n, mean[1] / n, mean[2] / n]; }
      while (picked.length < want && picked.length < scored.length) {
        var bestC = null; var bestCV = -1;
        scored.forEach(function (sc) {
          if (picked.indexOf(sc) !== -1) return;
          var c = rgb(map[sc.code].hex);
          var spread;
          if (!c) spread = 0.15;
          else if (!picked.length) spread = dist(c, mean);
          else {
            spread = 1;
            picked.forEach(function (pk) {
              var pc = rgb(map[pk.code].hex);
              if (pc) spread = Math.min(spread, dist(c, pc));
            });
          }
          var fit = maxScore ? sc.score / maxScore : 0.5;
          var val = spread * (0.35 + 0.65 * fit);
          if (val > bestCV) { bestCV = val; bestC = sc; }
        });
        if (!bestC) break;
        picked.push(bestC);
      }
    } else {
      while (picked.length < want && picked.length < scored.length) {
        var best = null; var bestVal = -1;
        scored.forEach(function (sc) {
          if (picked.indexOf(sc) !== -1) return;
          var colour = map[sc.code].name || sc.code;
          var val = sc.score * (usedColour[colour] ? 0.8 : 1);
          if (val > bestVal) { bestVal = val; best = sc; }
        });
        if (!best) break;
        picked.push(best);
        usedColour[map[best.code].name || best.code] = true;
      }
    }

    return {
      key: RESULT_KEY[q1] || 'famille',
      breeds: picked.map(function (x) { return x.code; }),
      detail: picked,
      weights: w
    };
  }

  /* Up to three plain reasons per breed, the ones that mattered most for
     this visitor's answers. Facts only, taken from the fiche. */
  function reasonsFor(info, pick, q1) {
    /* Line 1 is always the hard fact that tells breeds apart (eggs and
       shell colour); then the two traits that weighed most for this
       visitor. Identical filler across three picks helps nobody. */
    var out = [];
    var eggs = num(info.eggs);
    var colour = info.name ? info.name.toLowerCase() : '';
    if (eggs && colour) out.push(eggs + ' oeufs par année, coquille ' + colour);
    else if (eggs) out.push(eggs + ' oeufs par année');
    else if (colour) out.push('coquille ' + colour);

    var cands = [];
    if (info.dual) cands.push({ k: 'dual', txt: 'double usage : oeufs et chair' });
    if (num(info.kid) >= 5) cands.push({ k: 'kid', txt: 'très douce avec les enfants' });
    else if (num(info.kid) === 4) cands.push({ k: 'kid', txt: 'douce avec les enfants' });
    if (num(info.cold) >= 5) cands.push({ k: 'cold', txt: "très rustique l'hiver" });
    else if (num(info.cold) === 4) cands.push({ k: 'cold', txt: "rustique l'hiver" });
    if (num(info.noise) >= 4) cands.push({ k: 'quiet', txt: 'discrète' });
    if (num(info.doc) >= 5) cands.push({ k: 'doc', txt: 'très calme, se manipule bien' });
    else if (num(info.doc) === 4) cands.push({ k: 'doc', txt: 'calme' });
    if (info.care === 'debutant') cands.push({ k: 'easy', txt: 'facile pour débuter' });
    cands.sort(function (x, y) { return (pick.parts[y.k] || 0) - (pick.parts[x.k] || 0); });
    cands.forEach(function (c) { if (out.length < 3 && (pick.parts[c.k] || 0) > 0) out.push(c.txt); });
    return out;
  }

  var quizFlock = null; /* breed codes of the current recommendation, for the CTA highlight */

  function clearQuizHighlight() {
    var g = grid();
    if (!g) return;
    g.removeAttribute('data-jpc-quiz-dim');
    g.querySelectorAll('[data-jpc-card].is-quiz-pick').forEach(function (c) { c.classList.remove('is-quiz-pick'); });
  }

  function highlightQuizCards(breeds, eggNames) {
    var g = grid();
    if (!g) return;
    var codes = {}; breeds.forEach(function (b) { codes[b] = true; });
    var eggs = {}; eggNames.forEach(function (n) { if (n) eggs[n] = true; });
    var hit = 0;
    g.querySelectorAll('[data-jpc-card]').forEach(function (card) {
      var code = card.getAttribute('data-breed-code');
      var egg = card.getAttribute('data-egg');
      var match = (code && codes[code]) || (egg && eggs[egg]);
      if (match) { card.classList.add('is-quiz-pick'); hit++; }
      else { card.classList.remove('is-quiz-pick'); }
    });
    if (hit) {
      g.setAttribute('data-jpc-quiz-dim', 'true');
      scrollToEl(g);
      setTimeout(clearQuizHighlight, 2600);
    } else {
      scrollToEl(g);
    }
  }

  function initQuiz() {
    var quiz = document.querySelector('[data-jpc-quiz]');
    if (!quiz) return;

    var steps = [].slice.call(quiz.querySelectorAll('[data-jpc-q]'));
    var resultWrap = quiz.querySelector('[data-jpc-quiz-result-wrap]');
    var panels = [].slice.call(quiz.querySelectorAll('[data-jpc-result]'));
    var bar = quiz.querySelector('[data-jpc-quiz-bar]');
    var countLabel = quiz.querySelector('[data-jpc-quiz-count]');
    var backBtn = quiz.querySelector('[data-jpc-quiz-back]');
    var live = quiz.querySelector('[data-jpc-quiz-live]');
    var map = parseJSON(quiz.querySelector('[data-jpc-quiz-map]')) || {};
    var feed = parseJSON(quiz.querySelector('[data-jpc-quiz-feed]')) || null;

    /* ---------- one-tap flock reservation ---------- */
    function fmtMoney(cents) {
      return (cents / 100).toFixed(2).replace('.', ',') + ' $';
    }
    /* Hens per breed so the flock lands in the size the visitor asked
       for, whatever the number of breeds picked (2, 3 or 4). */
    function qtyFor(q2, n) {
      n = n || 3;
      if (q2 === 'grande') return Math.max(1, Math.ceil(9 / n));
      if (q2 === 'moyenne') return Math.max(1, Math.round(6 / n));
      return Math.max(1, Math.round(4 / n));
    }
    var SIZE_LABEL = { petite: 'Pour 3 à 4 poules', moyenne: 'Pour 5 à 8 poules', grande: 'Pour 9 poules et plus' };

    function quizAddItems(items) {
      return new Promise(function (resolve, reject) {
        if (!items || !items.length) { reject(new Error('empty')); return; }
        var body = { items: items };
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          body.sections = cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
          body.sections_url = window.location.pathname;
          if (typeof cartDrawer.setActiveElement === 'function') cartDrawer.setActiveElement(document.activeElement);
        }
        var controller = new AbortController();
        var timeout = setTimeout(function () { controller.abort(); }, 8000);
        var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
        fetch(addUrl, {
          method: 'POST',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        })
          .then(function (r) { clearTimeout(timeout); return r.json().then(function (data) { return { ok: r.ok, data: data }; }); })
          .then(function (res) {
            if (!res.ok || (res.data && res.data.status && res.data.status !== 200)) {
              throw new Error((res.data && (res.data.description || res.data.message)) || 'Erreur');
            }
            if (typeof window.publish === 'function' && typeof window.PUB_SUB_EVENTS !== 'undefined') {
              window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'jp-quiz' });
            }
            var drawer = document.querySelector('cart-drawer');
            if (drawer) {
              if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
              if (typeof drawer.renderContents === 'function') drawer.renderContents(res.data);
            }
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            resolve(res.data);
          })
          .catch(function (err) { clearTimeout(timeout); reject(err); });
      });
    }

    function openCart() {
      var bubble = document.querySelector('#cart-icon-bubble');
      if (bubble) bubble.click(); else window.location.href = '/cart';
    }

    /* One reservation path for the quiz flock and the rainbow basket:
       adds the lines, adds the required feed bag when the cart has none
       and the customer is not exempt, remembers the store and day. */
    function reserve(lines, per, chosen, label, go, msg, onDone) {
      go.disabled = true;
      go.textContent = 'Réservation en cours';
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var items = lines.map(function (v) { return { id: parseInt(v.id, 10), quantity: per }; });
          var feedBypass = quiz.getAttribute('data-feed-bypass') === 'true';
          if (!feedBypass && feed && feed.preferred && feed.ids && feed.ids.length) {
            var feedIds = feed.ids.map(String);
            var hasFeed = (cart.items || []).some(function (li) { return feedIds.indexOf(String(li.variant_id)) !== -1; });
            if (!hasFeed) items.push({ id: parseInt(feed.preferred.id, 10), quantity: 1 });
          }
          return quizAddItems(items);
        })
        .then(function () {
          try { localStorage.setItem('jp-event-selection', JSON.stringify({ store: chosen, date: label })); } catch (e) { /* noop */ }
          go.textContent = 'Troupeau réservé · voir le panier';
          go.disabled = false;
          go._jpDone = true; /* a second tap opens the cart, it never adds twice */
          if (onDone) onDone();
        })
        .catch(function () {
          go.disabled = false;
          go.textContent = 'Réessayer';
          if (msg) { msg.hidden = false; msg.textContent = 'Une erreur est survenue. Réessayez ou réservez chaque race sur sa page.'; }
        });
    }

    function escHtml(s) {
      return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function renderQuizAtc(panel, breeds, q2) {
      var box = panel.querySelector('[data-jpc-quiz-atc]');
      if (!box) return;
      box.innerHTML = '';
      var per = qtyFor(q2, breeds.length);
      var buyable = [];
      var missing = [];
      breeds.forEach(function (code) {
        var e = map[code];
        if (!e) return;
        if (e.variants && e.variants.length) buyable.push(e);
        else missing.push(e.title || code);
      });
      var noteNone = quiz.getAttribute('data-note-none') || '';
      var noteLater = quiz.getAttribute('data-note-later') || '';
      var wlLabel = quiz.getAttribute('data-waitlist-label') || '';
      var wlTarget = quiz.getAttribute('data-waitlist-target') || '#jpp-waitlist';
      function waitlistLink() {
        if (!wlLabel) return '';
        /* only offer the anchor when the page actually has that target */
        if (wlTarget.charAt(0) === '#' && !document.querySelector(wlTarget)) return '';
        return '<a class="jpc-btn jpc-btn--primary jpc-quiz__atc-btn" href="' + wlTarget + '">' + wlLabel + '</a>';
      }
      if (!buyable.length) {
        box.hidden = false;
        box.innerHTML = (noteNone ? '<p class="jpc-quiz__atc-note">' + noteNone + '</p>' : '') + waitlistLink();
        return;
      }
      var pds = [];
      buyable.forEach(function (e) { e.variants.forEach(function (v) { if (v.pd) pds.push(v.pd); }); });
      pds.sort();
      var target = pds[0] || null;
      function candidates(e) {
        return e.variants.filter(function (v) { return !target || v.pd === target || !v.pd; });
      }
      var stores = [];
      buyable.forEach(function (e) {
        candidates(e).forEach(function (v) { if (stores.indexOf(v.store) < 0) stores.push(v.store); });
      });
      var chosen = stores[0];
      try {
        var saved = JSON.parse(localStorage.getItem('jp-event-selection') || 'null');
        if (saved && stores.indexOf(saved.store) >= 0) chosen = saved.store;
      } catch (e) { /* noop */ }

      function pick(e) {
        var cs = candidates(e);
        for (var i = 0; i < cs.length; i++) { if (cs[i].store === chosen) return cs[i]; }
        return cs[0];
      }
      function build() {
        var lines = buyable.map(pick).filter(Boolean);
        var label = lines[0] ? lines[0].label : '';
        var total = 0;
        lines.forEach(function (v) { total += (v.price || 0) * per; });
        var html = '';
        if (stores.length > 1) {
          html += '<div class="jpc-quiz__atc-stores">' + stores.map(function (s) {
            return '<button type="button" class="jpc-quiz__atc-store' + (s === chosen ? ' is-selected' : '') + '" data-jpc-atc-store="' + s.replace(/"/g, '&quot;') + '">' + s + '</button>';
          }).join('') + '</div>';
        }
        html += '<button type="button" class="jpc-btn jpc-btn--primary jpc-quiz__atc-btn" data-jpc-atc-go>' +
          'Tout réserver pour le ' + label + ' · ' + (per * lines.length) + ' poules · ' + fmtMoney(total) + '</button>';
        html += '<p class="jpc-quiz__atc-sub">Un sac de moulée est requis : un seul par saison. Il s’ajoute si vous n’en avez pas déjà un.</p>';
        var gift = quiz.getAttribute('data-rainbow-gift') || '';
        var seenColour = {}; var colourN = 0;
        buyable.forEach(function (e) { if (e.name && !seenColour[e.name]) { seenColour[e.name] = true; colourN++; } });
        if (gift && colourN >= 3) {
          html += '<p class="jpc-quiz__atc-gift">Panier arc-en-ciel : ' + colourN + ' couleurs d’oeufs. Au ramassage : ' + escHtml(gift) + '.</p>';
        }
        if (missing.length) {
          html += '<p class="jpc-quiz__atc-note">' + (noteLater ? noteLater + ' ' : '') + missing.join(', ') + '.</p>';
        }
        html += '<p class="jpc-quiz__atc-msg" data-jpc-atc-msg hidden></p>';
        box.innerHTML = html;
        box.hidden = false;
        box.querySelectorAll('[data-jpc-atc-store]').forEach(function (b) {
          b.addEventListener('click', function () { chosen = b.getAttribute('data-jpc-atc-store'); build(); });
        });
        var go = box.querySelector('[data-jpc-atc-go]');
        var msg = box.querySelector('[data-jpc-atc-msg]');
        if (go) go.addEventListener('click', function () {
          if (go._jpDone) { openCart(); return; }
          reserve(lines, per, chosen, label, go, msg, function () {
            ga('jpc_quiz_add', { flock: breeds.join(','), qty_total: per * lines.length, store: chosen });
          });
        });
      }
      build();
    }

    /* ---------- 5. One-click rainbow basket ---------- */
    /* Three breeds, three different egg colours, same pickup day and
       store. For each day and store that can do it: start from the breed
       with the most birds left, then take the egg colour furthest from
       those already chosen (ties go to the breed with more stock, which
       quietly moves the slower sellers). The earliest such day wins. */
    function rainbowTrio(entries) {
      var byName = {};
      entries.forEach(function (en) {
        var n = en.info.name || en.code;
        if (!byName[n] || (en.v.q || 0) > (byName[n].v.q || 0)) byName[n] = en;
      });
      var cands = Object.keys(byName).map(function (n) { return byName[n]; });
      if (cands.length < 3) return null;
      cands.sort(function (a, b) { return (b.v.q || 0) - (a.v.q || 0); });
      var picked = [cands[0]];
      while (picked.length < 3) {
        var best = null; var bestVal = -1;
        cands.forEach(function (c) {
          if (picked.indexOf(c) !== -1) return;
          var lab = rgb(c.info.hex);
          var spread = 1;
          picked.forEach(function (pk) {
            var pl = rgb(pk.info.hex);
            spread = Math.min(spread, (lab && pl) ? dist(lab, pl) : 0.15);
          });
          var val = spread + Math.min(c.v.q || 0, 200) / 100000; /* stock only breaks ties */
          if (val > bestVal) { bestVal = val; best = c; }
        });
        if (!best) return null;
        picked.push(best);
      }
      return picked;
    }

    function initRainbowBasket() {
      var box = document.querySelector('[data-jpc-rainbow-basket]');
      if (!box) return;
      var body = box.querySelector('[data-jpc-rainbow-basket-body]');
      if (!body) return;
      var combos = {};
      Object.keys(map).forEach(function (code) {
        var info = map[code];
        (info.variants || []).forEach(function (v) {
          var key = (v.pd || 'zzzz') + '|' + v.store;
          if (!combos[key]) combos[key] = { pd: v.pd || 'zzzz', store: v.store, label: v.label, entries: [] };
          combos[key].entries.push({ code: code, info: info, v: v });
        });
      });
      var options = [];
      Object.keys(combos).forEach(function (k) {
        var trio = rainbowTrio(combos[k].entries);
        if (trio) options.push({ pd: combos[k].pd, store: combos[k].store, label: combos[k].label, trio: trio });
      });
      if (!options.length) { box.hidden = true; return; }
      options.sort(function (a, b) { return a.pd < b.pd ? -1 : (a.pd > b.pd ? 1 : 0); });
      var day = options[0].pd;
      options = options.filter(function (o) { return o.pd === day; });
      var chosen = options[0];
      try {
        var saved = JSON.parse(localStorage.getItem('jp-event-selection') || 'null');
        if (saved) options.forEach(function (o) { if (o.store === saved.store) chosen = o; });
      } catch (e) { /* noop */ }
      var gift = box.getAttribute('data-gift') || '';
      var btnLabel = box.getAttribute('data-btn-label') || 'Réserver le panier arc-en-ciel';

      function build() {
        var total = 0;
        chosen.trio.forEach(function (t) { total += t.v.price || 0; });
        var html = '<ul class="jpc-rainbow__basket-list">' + chosen.trio.map(function (t) {
          return '<li><span class="jpc-rainbow__basket-sw" style="background:' + escHtml(t.info.hex || '#EDE6D8') + '" aria-hidden="true"></span>' +
            '<span><b>' + escHtml(t.info.title || t.code) + '</b>' + (t.info.name ? ' · ' + escHtml(t.info.name) : '') + '</span></li>';
        }).join('') + '</ul>';
        if (options.length > 1) {
          html += '<div class="jpc-quiz__atc-stores">' + options.map(function (o) {
            return '<button type="button" class="jpc-quiz__atc-store' + (o === chosen ? ' is-selected' : '') + '" data-jpc-rb-store="' + escHtml(o.store) + '">' + escHtml(o.store) + '</button>';
          }).join('') + '</div>';
        }
        html += '<button type="button" class="jpc-btn jpc-btn--primary jpc-quiz__atc-btn" data-jpc-rb-go>' +
          escHtml(btnLabel) + ' · ' + escHtml(chosen.label || '') + ' · 3 poules · ' + fmtMoney(total) + '</button>';
        html += '<p class="jpc-quiz__atc-sub">Un sac de moulée est requis : un seul par saison. Il s’ajoute si vous n’en avez pas déjà un.</p>';
        if (gift) html += '<p class="jpc-quiz__atc-gift">Au ramassage : ' + escHtml(gift) + '.</p>';
        html += '<p class="jpc-quiz__atc-msg" data-jpc-rb-msg hidden></p>';
        body.innerHTML = html;
        body.querySelectorAll('[data-jpc-rb-store]').forEach(function (b) {
          b.addEventListener('click', function () {
            var s = b.getAttribute('data-jpc-rb-store');
            options.forEach(function (o) { if (o.store === s) chosen = o; });
            build();
          });
        });
        var go = body.querySelector('[data-jpc-rb-go]');
        var msg = body.querySelector('[data-jpc-rb-msg]');
        go.addEventListener('click', function () {
          if (go._jpDone) { openCart(); return; }
          var codes = chosen.trio.map(function (t) { return t.code; });
          reserve(chosen.trio.map(function (t) { return t.v; }), 1, chosen.store, chosen.label, go, msg, function () {
            ga('jpc_rainbow_basket_add', { flock: codes.join(','), store: chosen.store });
          });
        });
      }
      build();
      box.hidden = false;
    }

    var countTmpl = {
      petite: quiz.getAttribute('data-count-petite') || '',
      moyenne: quiz.getAttribute('data-count-moyenne') || '',
      grande: quiz.getAttribute('data-count-grande') || ''
    };

    var answers = { q1: null, q2: null, q3: null };
    var current = 0;
    var total = steps.length;
    var started = false;

    function showStep(idx) {
      steps.forEach(function (s, i) { s.setAttribute('data-active', i === idx ? 'true' : 'false'); });
      if (resultWrap) resultWrap.setAttribute('data-active', 'false');
      if (bar) bar.style.width = ((idx + 1) / total * 100) + '%';
      if (countLabel) countLabel.textContent = (idx + 1) + ' / ' + total;
      if (backBtn) backBtn.disabled = (idx === 0);
      current = idx;
    }

    var plannedPrefix = quiz.getAttribute('data-planned-prefix') || 'Prévue dès';
    var plannedSuffix = quiz.getAttribute('data-planned-suffix') || '';

    function buildChips(panel, flock) {
      var host = panel.querySelector('[data-jpc-chips]');
      if (!host) return;
      host.textContent = '';
      flock.detail.forEach(function (pick) {
        var info = map[pick.code];
        if (!info) return;
        var li = document.createElement('li');
        li.className = 'jpc-quiz__pick';

        var sw = document.createElement('span');
        sw.className = 'jpc-quiz__pick-swatch';
        sw.setAttribute('aria-hidden', 'true');
        if (info.hex) sw.style.background = info.hex;
        li.appendChild(sw);

        var body = document.createElement('div');
        body.className = 'jpc-quiz__pick-body';

        var a = document.createElement('a');
        a.className = 'jpc-quiz__pick-name';
        a.href = info.url || '#';
        a.textContent = info.title || pick.code;
        body.appendChild(a);
        if (info.isNew) {
          var nb = document.createElement('span');
          nb.className = 'jpc-quiz__pick-new';
          nb.textContent = 'Nouveau';
          body.appendChild(nb);
        }

        var why = reasonsFor(info, pick, answers.q1);
        if (why.length) {
          var p = document.createElement('p');
          p.className = 'jpc-quiz__pick-why';
          p.textContent = why.join(' · ');
          body.appendChild(p);
        }

        var when = document.createElement('p');
        when.className = 'jpc-quiz__pick-when';
        if (info.variants && info.variants.length) {
          when.textContent = 'Réservable maintenant';
          when.className += ' is-open';
        } else if (info.planned) {
          when.textContent = plannedPrefix + ' ' + info.planned + (plannedSuffix ? ' · ' + plannedSuffix : '');
        } else {
          when.textContent = '';
        }
        if (when.textContent) body.appendChild(when);

        li.appendChild(body);
        host.appendChild(li);
      });
    }

    function showResult() {
      var flock = pickFlock(answers, map);
      quizFlock = flock.breeds;
      var matched = null;
      panels.forEach(function (panel) {
        var isMatch = panel.getAttribute('data-jpc-result') === flock.key;
        panel.style.display = isMatch ? 'block' : 'none';
        if (isMatch) matched = panel;
      });
      if (!matched && panels.length) { matched = panels[panels.length - 1]; matched.style.display = 'block'; }

      var eggNames = [];
      if (matched) {
        buildChips(matched, flock);
        renderQuizAtc(matched, flock.breeds, answers.q2);
        var countEl = matched.querySelector('[data-jpc-count]');
        if (countEl) {
          var per = qtyFor(answers.q2, flock.breeds.length);
          var std = answers.q2 === 'grande' ? 3 : answers.q2 === 'moyenne' ? 2 : 1;
          /* the editable line covers the usual case; otherwise say the real number */
          var tmpl = (per === std && countTmpl[answers.q2]) ? countTmpl[answers.q2]
            : (SIZE_LABEL[answers.q2] || 'Pour votre troupeau') + ', prenez ' + per + ' de chaque race.';
          countEl.textContent = tmpl;
          countEl.hidden = !tmpl;
        }
        var cta = matched.querySelector('[data-jpc-quiz-cta]');
        if (cta) {
          flock.breeds.forEach(function (code) { if (map[code] && map[code].name) eggNames.push(map[code].name); });
          cta.onclick = function (e) {
            e.preventDefault();
            highlightQuizCards(quizFlock, eggNames);
          };
        }
      }

      steps.forEach(function (s) { s.setAttribute('data-active', 'false'); });
      if (resultWrap) resultWrap.setAttribute('data-active', 'true');
      if (bar) bar.style.width = '100%';
      if (live && matched) {
        live.textContent = '';
        setTimeout(function () {
          var h = matched.querySelector('[data-jpc-result-title]');
          if (h) live.textContent = h.textContent;
        }, 60);
      }
      ga('jpc_quiz_complete', { q1: answers.q1, q2: answers.q2, q3: answers.q3, flock: flock.breeds.join(',') });
    }

    quiz.querySelectorAll('[data-jpc-val]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = btn.closest('[data-jpc-q]');
        if (!step) return;
        var key = step.getAttribute('data-jpc-q'); /* q1 | q2 | q3 */
        answers[key] = btn.getAttribute('data-jpc-val');
        step.querySelectorAll('[data-jpc-val]').forEach(function (o) { o.setAttribute('aria-pressed', o === btn ? 'true' : 'false'); });
        if (!started) { started = true; ga('jpc_quiz_start', {}); }
        setTimeout(function () {
          if (current < total - 1) showStep(current + 1);
          else showResult();
        }, 200);
      });
    });

    if (backBtn) backBtn.addEventListener('click', function () { if (current > 0) showStep(current - 1); });

    quiz.querySelectorAll('[data-jpc-quiz-restart]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        answers = { q1: null, q2: null, q3: null };
        started = false;
        quizFlock = null;
        clearQuizHighlight();
        quiz.querySelectorAll('[data-jpc-val]').forEach(function (o) { o.setAttribute('aria-pressed', 'false'); });
        showStep(0);
      });
    });

    showStep(0);
    initRainbowBasket();
  }

  /* ---------- 6. Sold-out substitution (breed grid) ---------- */
  /* A sold-out card names the closest breed of the same kind that can
     still be reserved: egg colour first (it is what people choose on),
     then the four temperament scores. Facts from the fiches only. */
  function likeness(a, b) {
    var la = rgb(a.hex); var lb = rgb(b.hex);
    var d = (la && lb) ? dist(la, lb) * 2 : 0.6;
    ['kid', 'doc', 'cold', 'noise'].forEach(function (k) {
      var x = num(a[k]); var y = num(b[k]);
      d += (x === null || y === null) ? 0.2 : Math.abs(x - y) / 5;
    });
    return d;
  }

  function initSubstitutes() {
    var g = grid();
    if (!g) return;
    var label = g.getAttribute('data-sub-label') || '';
    if (!label) return;
    function read(card) {
      return {
        el: card,
        type: card.getAttribute('data-btype') || '',
        hex: card.getAttribute('data-hex') || '',
        kid: card.getAttribute('data-kid'), doc: card.getAttribute('data-doc'),
        cold: card.getAttribute('data-cold'), noise: card.getAttribute('data-noise'),
        title: card.getAttribute('data-title') || '', url: card.getAttribute('data-url') || '',
        code: card.getAttribute('data-breed-code') || '',
        sold: card.getAttribute('data-soldout') === 'true'
      };
    }
    var cards = [].slice.call(g.querySelectorAll('[data-jpc-card]')).map(read);
    var open = cards.filter(function (c) { return !c.sold && c.code && c.url; });
    if (!open.length) return;
    cards.forEach(function (c) {
      if (!c.sold || !c.code) return;
      var best = null; var bestD = Infinity;
      open.forEach(function (o) {
        if (o.type !== c.type) return;
        var d = likeness(c, o);
        if (d < bestD) { bestD = d; best = o; }
      });
      if (!best) return;
      var foot = c.el.querySelector('.jpc-card__foot');
      if (!foot || c.el.querySelector('.jpc-card__sub')) return;
      var p = document.createElement('p');
      p.className = 'jpc-card__sub';
      p.appendChild(document.createTextNode(label + ' '));
      var a = document.createElement('a');
      a.href = best.url;
      a.textContent = best.title;
      a.addEventListener('click', function () { ga('jpc_substitute_click', { from: c.code, to: best.code }); });
      p.appendChild(a);
      foot.parentNode.insertBefore(p, foot);
    });
  }

  /* ---------- 4. Availability matrix link GA ---------- */
  function initMatrixLinks() {
    var links = document.querySelectorAll('[data-jpc-matrix-link]');
    if (!links.length) return;
    links.forEach(function (link) {
      link.addEventListener('click', function () {
        ga('jpc_matrix_click', {
          breed: link.getAttribute('data-breed-code') || '',
          target_url: link.getAttribute('href') || ''
        });
      });
    });
  }

  ready(function () {
    initGridFilters();
    initRainbow();
    initQuiz();
    initSubstitutes();
    initMatrixLinks();
  });
})();

/* Event band countdown + GA view on collection pages. Same behaviour as
   jp-pdp.js initEventBand; the data-jp-band-init flag prevents double init
   when both scripts ever share a page. */
(function () {
  'use strict';
  var band = document.querySelector('[data-jp-event-band]');
  if (!band || band.dataset.jpBandInit) return;
  band.dataset.jpBandInit = '1';
  function ga(name, params) {
    if (typeof window.gtag === 'function') { try { window.gtag('event', name, params || {}); } catch (e) { /* noop */ } }
  }
  var gaName = band.getAttribute('data-jp-band-ga') || 'jp_event_band_view';
  var state = (band.className.match(/jpp-eventband--(\w+)/) || [])[1] || '';
  var fired = false;
  function fire() { if (fired) return; fired = true; ga(gaName, { state: state }); }
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) { if (entry.isIntersecting) { fire(); io.disconnect(); } });
    }, { threshold: 0.4 });
    io.observe(band);
  } else { fire(); }

  var cd = band.querySelector('[data-jp-band-countdown]');
  if (!cd) return;
  var iso = cd.getAttribute('data-jp-deadline');
  if (!iso) return;
  var end = Date.parse(iso);
  if (isNaN(end)) return;
  var timer = cd.querySelector('[data-jp-band-timer]');
  var elD = cd.querySelector('[data-jp-band-days]');
  var elH = cd.querySelector('[data-jp-band-hours]');
  var elM = cd.querySelector('[data-jp-band-mins]');
  function tick() {
    var diff = end - Date.now();
    if (diff <= 0) { if (timer) timer.hidden = true; cd.classList.add('is-expired'); return false; }
    var s = Math.floor(diff / 1000);
    var d = Math.floor(s / 86400); s -= d * 86400;
    var h = Math.floor(s / 3600); s -= h * 3600;
    var m = Math.floor(s / 60);
    if (elD) elD.textContent = d;
    if (elH) elH.textContent = h;
    if (elM) elM.textContent = m;
    if (timer) timer.hidden = false;
    if (diff < 86400000) cd.classList.add('is-urgent');
    return true;
  }
  if (tick() === false) return;
  var t = setInterval(function () { if (tick() === false) clearInterval(t); }, 60000);
})();

/* Mobile sticky reserve/resume bar (collection). Default state anchors to
   the grid; when the cart already holds birds from this collection it
   becomes a resume-your-reservation bar opening the cart drawer. */
(function () {
  'use strict';
  var bar = document.querySelector('[data-jpc-sticky]');
  if (!bar || bar.dataset.jpcStickyInit) return;
  bar.dataset.jpcStickyInit = '1';

  var txt = bar.querySelector('[data-jpc-sticky-txt]');
  var btn = bar.querySelector('[data-jpc-sticky-btn]');

  // Reveal after the hero scrolls out; hide near the footer.
  var hero = document.querySelector('[data-jpc-hero]');
  function onScroll() {
    var past = hero ? hero.getBoundingClientRect().bottom < 0 : window.scrollY > 600;
    var nearEnd = (window.innerHeight + window.scrollY) > (document.body.scrollHeight - 400);
    bar.setAttribute('data-visible', past && !nearEnd ? 'true' : 'false');
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();

  // Resume state when the cart already holds birds from this collection.
  var handles = {};
  document.querySelectorAll('[data-jpc-card] a[href*="/products/"]').forEach(function (a) {
    var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    if (m) handles[m[1]] = true;
  });
  function toResume(count) {
    if (txt) txt.textContent = 'Votre troupeau vous attend · ' + count + (count > 1 ? ' oiseaux' : ' oiseau');
    if (btn) {
      btn.textContent = 'Voir le panier';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var bubble = document.querySelector('#cart-icon-bubble');
        if (bubble) bubble.click(); else window.location.href = '/cart';
      });
    }
  }
  function checkCart() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        var n = 0;
        (c.items || []).forEach(function (li) { if (handles[li.handle]) n += li.quantity; });
        if (n > 0) toResume(n);
      })
      .catch(function () { /* noop */ });
  }
  checkCart();
  document.addEventListener('cart:refresh', checkCart);
})();
